/**
 * Invoice Processing Service
 * 
 * Provides API methods for invoice processing operations
 * Integrates with the Invoice Processing System API
 */
import { apiService } from './api.service';
import { 
  INVOICE_API_BASE_URL, 
  INVOICE_API_KEY, 
  INVOICE_API_TIMEOUT 
} from '../config/api.config';
import axios from 'axios';

// Invoice Processing API Configuration
const INVOICE_API_BASE = INVOICE_API_BASE_URL; // Invoice processing system API
const INVOICE_API_KEY_VALUE = INVOICE_API_KEY; // From configuration

// Create a separate axios instance for invoice processing API
const invoiceApiClientInstance = axios.create({
  baseURL: INVOICE_API_BASE,
  timeout: INVOICE_API_TIMEOUT,
  headers: {
    'X-API-Key': INVOICE_API_KEY_VALUE,
    'Content-Type': 'application/json'
  }
});

// Add request interceptor for invoice API
invoiceApiClientInstance.interceptors.request.use(
  (config) => {
    console.log('Invoice API Request:', {
      url: config.url,
      method: config.method,
      hasApiKey: !!INVOICE_API_KEY_VALUE
    });
    return config;
  },
  (error) => {
    console.error('Invoice API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
invoiceApiClientInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Invoice API Response Error:', error);
    return Promise.reject(error);
  }
);

/**
 * Invoice Processing Service
 */
export const invoiceService = {
  /**
   * Upload a single invoice file
   * @param {File} file - The invoice PDF file
   * @returns {Promise<Object>} Upload response with document_id
   */
  async uploadInvoice(file) {
    try {
      if (!file || !file.type.includes('pdf')) {
        throw new Error('Only PDF files are allowed');
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await invoiceApiClientInstance.post('/api/v1/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Invoice upload failed:', error);
      throw new Error(error.response?.data?.detail || 'Upload failed');
    }
  },

  /**
   * Upload multiple invoice files (batch upload)
   * @param {File[]} files - Array of invoice PDF files
   * @returns {Promise<Object>} Batch upload response
   */
  async uploadInvoicesBatch(files) {
    try {
      const formData = new FormData();
      files.forEach(file => {
        if (file.type.includes('pdf')) {
          formData.append('files', file);
        }
      });

      const response = await invoiceApiClientInstance.post('/api/v1/upload/batch', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Batch invoice upload failed:', error);
      throw new Error(error.response?.data?.detail || 'Batch upload failed');
    }
  },

  /**
   * Process a document through the AI pipeline
   * @param {string} documentId - The document ID to process
   * @returns {Promise<Object>} Processing response
   */
  async processDocument(documentId) {
    try {
      const response = await invoiceApiClientInstance.post(
        `/api/v1/process/${documentId}`,
        null,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Document processing failed:', error);
      throw new Error(error.response?.data?.detail || 'Processing failed');
    }
  },

  /**
   * Process a document synchronously with progress tracking
   * @param {string} documentId - The document ID to process
   * @param {Function} onProgress - Progress callback function
   * @returns {Promise<Object>} Processing response with results
   */
  async processDocumentSync(documentId, onProgress = null) {
    try {
      const startTime = Date.now();
      
      // Start processing
      const response = await invoiceApiClientInstance.post(
        `/api/v1/process/${documentId}`,
        null,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const processingTime = Date.now() - startTime;
      
      // Get the processed document metadata
      const metadataResponse = await this.getDocumentMetadata(documentId);
      
      const result = {
        ...response.data,
        processing_time: `${processingTime}ms`,
        metadata: metadataResponse.metadata,
        document_id: documentId
      };

      if (onProgress) {
        onProgress(100, 'Processing completed');
      }

      return result;
    } catch (error) {
      console.error('Synchronous document processing failed:', error);
      throw new Error(error.response?.data?.detail || 'Processing failed');
    }
  },

  /**
   * Process all pending documents
   * @returns {Promise<Object>} Batch processing response
   */
  async processAllDocuments() {
    try {
      const response = await invoiceApiClientInstance.post('/api/v1/process/all', null, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Batch processing failed:', error);
      throw new Error(error.response?.data?.detail || 'Batch processing failed');
    }
  },

  /**
   * Get all documents with optional basket filtering
   * @param {string} basket - Optional basket name to filter by
   * @returns {Promise<Object>} Documents list
   */
  async getDocuments(basket = null) {
    try {
      const params = basket ? { basket } : {};
      const response = await invoiceApiClientInstance.get('/api/v1/documents', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch documents');
    }
  },

  /**
   * Get document metadata by ID
   * @param {string} documentId - The document ID
   * @returns {Promise<Object>} Document metadata
   */
  async getDocumentMetadata(documentId) {
    try {
      const response = await invoiceApiClientInstance.get(`/api/v1/documents/${documentId}/metadata`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch document metadata:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch document metadata');
    }
  },

  /**
   * Get all baskets with document counts
   * @returns {Promise<Object>} Baskets list
   */
  async getBaskets() {
    try {
      const response = await invoiceApiClientInstance.get('/api/v1/baskets');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch baskets:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch baskets');
    }
  },

  /**
   * Get processing status for a document
   * @param {string} documentId - The document ID
   * @returns {Promise<Object>} Processing status
   */
  async getProcessingStatus(documentId) {
    try {
      const response = await invoiceApiClientInstance.get(`/api/v1/process/status/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch processing status:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch processing status');
    }
  },

  /**
   * Get raw responses from original document
   * @param {string} documentId - The original document ID
   * @returns {Promise<Object>} Raw responses data
   */
  async getRawResponses(documentId) {
    try {
      const response = await invoiceApiClientInstance.get(`/api/v1/documents/${documentId}/raw-responses`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch raw responses:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch raw responses');
    }
  },

  /**
   * Check API health
   * @returns {Promise<Object>} Health check response
   */
  async checkHealth() {
    try {
      const response = await invoiceApiClientInstance.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw new Error('Invoice processing API is not available');
    }
  }
};

// Export the raw axios instance for advanced use cases
export const invoiceApiClient = invoiceApiClientInstance;

// Export constants for use in components
export const INVOICE_API_CONFIG = {
  baseURL: INVOICE_API_BASE,
  apiKey: INVOICE_API_KEY_VALUE,
  timeout: INVOICE_API_TIMEOUT
};

// Available baskets for filtering
export const INVOICE_BASKETS = {
  RAW_INVOICES: 'raw_invoices',
  PROCESSED_DOCUMENTS: 'processed_documents',
  READY_TO_PAY: 'ready_to_pay',
  FURTHER_VALIDATION: 'further_validation'
};

// Processing statuses
export const PROCESSING_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

// PO validation statuses
export const PO_VALIDATION_STATUS = {
  VALID: 'valid',
  INVALID: 'invalid',
  PENDING: 'pending'
};
